import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Newspaper, Radio, Video, CalendarDays, Users } from "lucide-react";
import api from "@/lib/api";

/* ------------------------------------------------------------------ */
/* Page data                                                           */
/* ------------------------------------------------------------------ */

const AUDIENCE = [
  { label: "Founders", body: "Building companies from zero, past product-market fit, or into their second decade." },
  { label: "Owners", body: "Second- and third-generation owners of established businesses across industries." },
  { label: "Executives", body: "COOs, CFOs, and general managers running day-to-day operating decisions." },
  { label: "Managers", body: "First-time managers, department leads, and operators inside larger organizations." },
  { label: "Ambitious professionals", body: "Specialists and advisors who want the operator's view of the business, not the pundit's." },
];

const FORMATS = [
  {
    icon: Mail,
    label: "Newsletter placements",
    body: "Clearly labeled sponsor slot inside The Operating Class Newsletter. One sponsor per issue. Copy reviewed by editorial for tone.",
  },
  {
    icon: Newspaper,
    label: "Branded editorial series",
    body: "Multi-piece series on an operating theme (e.g. hiring, cash flow, systems). Sponsor is disclosed on every piece. Editorial retains angle and standards.",
  },
  {
    icon: Radio,
    label: "Podcast & audio sponsorship",
    body: "Available once the audio format launches. Pre-roll or single-sponsor episodes with an editorially-approved read.",
  },
  {
    icon: Video,
    label: "Video partnerships",
    body: "Presenting-sponsor slots for individual video conversations or short-form operator lessons.",
  },
  {
    icon: CalendarDays,
    label: "Event partnerships",
    body: "Presenting or supporting partner for future roundtables, dinners, and live conversations with operators.",
  },
  {
    icon: Users,
    label: "Custom collaborations",
    body: "Research reports, benchmark studies, or long-running series built with a partner where the fit is genuine.",
  },
];

const BOUNDARIES = [
  "Sponsored content is always clearly labeled on the site.",
  "Editorial angle, guest selection, and questions remain with Operating Class.",
  "We do not run unlabeled paid coverage, native advertising disguised as editorial, or gotcha placements.",
  "We decline partnerships that require compromising a guest, misleading readers, or bending the standard.",
  "Sponsor conversations are handled by a separate contact from editorial application review.",
];

const initial = {
  company: "", contact_name: "", email: "", role: "",
  website: "", company_size: "", industry: "",
  interested_formats: "", budget_range: "", timing: "", message: "",
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function Partner() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partnerships", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div data-testid="partner-success" className="max-w-reading mx-auto px-6 py-32 text-center">
        <div className="eyebrow text-oc-coral mb-6">Received</div>
        <h1 className="font-editorial text-4xl md:text-5xl text-oc-ink leading-tight">
          Thank you. We have your inquiry.
        </h1>
        <p className="mt-8 font-editorial text-lg text-oc-ink-soft leading-relaxed">
          Sponsorship and partnership conversations are handled personally and kept separate from editorial. Someone will get back to you within a few business days, usually with a current audience snapshot and available formats for the period you&#39;re interested in.
        </p>
      </div>
    );

  return (
    <div data-testid="partner-page">
      {/* HERO */}
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-9">
              <div className="eyebrow text-oc-coral mb-8">Partner &amp; Sponsor</div>
              <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink max-w-4xl">
                Reach an audience of operators. Support work that respects them.
              </h1>
              <p className="mt-8 font-editorial text-xl md:text-2xl text-oc-ink-soft max-w-3xl leading-snug">
                Operating Class is read by founders, owners, executives, and managers looking for useful lessons from people with real operating experience. We work with a small number of partners each quarter, on terms that keep editorial independent and sponsorship clearly labeled.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#inquiry-form"
                  data-testid="partner-hero-inquire"
                  className="inline-flex items-center h-14 px-8 bg-oc-blue text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-blue-hover transition-colors"
                >
                  Start the conversation <ArrowRight className="w-4 h-4 ml-3" />
                </a>
                <a
                  href="#formats"
                  className="inline-flex items-center h-14 px-8 border border-oc-ink text-oc-ink font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-ink hover:text-white transition-colors"
                >
                  See available formats
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="border-b border-oc-hairline bg-oc-sand" data-testid="partner-audience">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow text-oc-coral mb-6">Audience</div>
            <h2 className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight text-oc-ink">
              Who reads Operating Class.
            </h2>
            <p className="mt-6 font-editorial text-lg md:text-xl text-oc-ink-soft leading-snug max-w-2xl">
              A serious readership of people responsible for making organizations work. We share current audience metrics privately, on request, and update them each quarter. If you&#39;re considering a partnership, ask for the latest snapshot and we&#39;ll send it directly.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {AUDIENCE.map((a, i) => (
              <div
                key={a.label}
                className="p-6 md:p-7 bg-white border border-oc-hairline"
                data-testid={`partner-audience-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-ui font-bold text-oc-coral text-xs uppercase tracking-widest mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-editorial text-xl text-oc-ink leading-snug">{a.label}</div>
                <p className="mt-3 font-editorial text-base text-oc-ink-soft leading-relaxed">
                  {a.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section id="formats" className="border-b border-oc-hairline scroll-mt-24" data-testid="partner-formats">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow text-oc-coral mb-6">Available formats</div>
            <h2 className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight text-oc-ink">
              Six ways to work with us.
            </h2>
            <p className="mt-6 font-editorial text-lg md:text-xl text-oc-ink-soft leading-snug max-w-2xl">
              Some formats are live today. Others open as new formats launch. We&#39;ll tell you plainly what&#39;s available now.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FORMATS.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.label}
                  className="p-8 bg-white border border-oc-hairline"
                  data-testid={`partner-format-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  <Icon className="w-6 h-6 text-oc-blue mb-6" />
                  <div className="font-editorial text-2xl text-oc-ink leading-tight">
                    {f.label}
                  </div>
                  <p className="mt-3 font-editorial text-base text-oc-ink-soft leading-relaxed">
                    {f.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDITORIAL BOUNDARIES */}
      <section className="border-b border-oc-hairline bg-oc-ink text-white" data-testid="partner-boundaries">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow text-oc-coral mb-6">Editorial boundaries</div>
              <h2 className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight text-white">
                Sponsorship supports the work. It doesn&#39;t bend it.
              </h2>
              <p className="mt-6 font-editorial text-lg text-white/75 leading-snug">
                These lines protect readers, contributors, and partners equally. They&#39;re the reason serious operators trust the publication.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {BOUNDARIES.map((b, i) => (
                  <li key={i} className="flex gap-4 border-b border-white/10 pb-5">
                    <span className="font-ui font-bold text-oc-coral text-xs mt-1.5 w-7 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-editorial text-lg text-white/90 leading-snug">
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquiry-form" className="scroll-mt-24">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-8">
                <div>
                  <div className="eyebrow text-oc-coral mb-4">Start the conversation</div>
                  <h2 className="font-editorial text-3xl md:text-4xl leading-tight text-oc-ink">
                    Tell us what you&#39;re thinking.
                  </h2>
                  <p className="mt-6 font-editorial text-base text-oc-ink-soft leading-relaxed">
                    Share a little about your company and what you&#39;re hoping to achieve. We&#39;ll respond with a current audience snapshot and honest availability, usually within a few business days.
                  </p>
                </div>
                <div className="border-l-2 border-oc-blue pl-6 py-2">
                  <div className="eyebrow text-oc-ink-muted mb-3">Not sure it&#39;s a fit?</div>
                  <p className="font-editorial text-base text-oc-ink leading-snug">
                    Send us a note anyway. Some of our best partnerships started as a five-line email.
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral"
                  >
                    General contact <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="lg:col-span-8 space-y-8" data-testid="partner-form">
              <FieldGroup title="Company">
                <Field label="Company" required value={form.company} onChange={set("company")} testId="pt-company" />
                <Field label="Industry" value={form.industry} onChange={set("industry")} testId="pt-industry" />
                <Field label="Company size" value={form.company_size} onChange={set("company_size")} testId="pt-company-size" placeholder="e.g. 250 employees" />
                <Field label="Website" value={form.website} onChange={set("website")} testId="pt-website" />
              </FieldGroup>

              <FieldGroup title="Your details">
                <Field label="Your name" required value={form.contact_name} onChange={set("contact_name")} testId="pt-contact-name" />
                <Field label="Email" required type="email" value={form.email} onChange={set("email")} testId="pt-email" />
                <Field label="Your role" value={form.role} onChange={set("role")} testId="pt-role" />
              </FieldGroup>

              <FieldGroup title="What you&#39;re considering">
                <div className="col-span-full">
                  <label className="eyebrow text-oc-ink-muted mb-2 block">Interested formats</label>
                  <select
                    data-testid="pt-formats"
                    value={form.interested_formats}
                    onChange={set("interested_formats")}
                    className="w-full h-12 px-4 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base"
                  >
                    <option value="">Not sure yet</option>
                    <option value="newsletter">Newsletter placement</option>
                    <option value="branded_series">Branded editorial series</option>
                    <option value="podcast">Podcast / audio (when available)</option>
                    <option value="video">Video partnership (when available)</option>
                    <option value="event">Event partnership</option>
                    <option value="custom">Custom collaboration</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow text-oc-ink-muted mb-2 block">Budget range</label>
                  <select
                    data-testid="pt-budget"
                    value={form.budget_range}
                    onChange={set("budget_range")}
                    className="w-full h-12 px-4 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base"
                  >
                    <option value="">Prefer to discuss</option>
                    <option value="under_5k">Under $5,000</option>
                    <option value="5k_15k">$5,000 to $15,000</option>
                    <option value="15k_50k">$15,000 to $50,000</option>
                    <option value="50k_plus">$50,000 and above</option>
                  </select>
                </div>
                <Field label="Timing" value={form.timing} onChange={set("timing")} testId="pt-timing" placeholder="e.g. This quarter, H1 2026" />
                <Textarea
                  label="What are you hoping to achieve?"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  testId="pt-message"
                  placeholder="A few sentences on your company, the audience you want to reach, and what a good outcome looks like."
                />
              </FieldGroup>

              <div className="pt-4">
                <button
                  data-testid="partner-submit"
                  type="submit"
                  disabled={submitting}
                  className="h-14 px-10 bg-oc-blue text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-blue-hover disabled:opacity-60 transition-colors"
                >
                  {submitting ? "Sending..." : "Submit inquiry"}
                </button>
                <p className="mt-4 font-editorial text-sm text-oc-ink-muted">
                  We read every partnership inquiry personally. Response usually within a few business days.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Form primitives                                                     */
/* ------------------------------------------------------------------ */

function FieldGroup({ title, children }) {
  return (
    <div className="pb-8 border-b border-oc-hairline">
      <div className="eyebrow text-oc-coral mb-6">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <label className="block col-span-1">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full h-12 px-4 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, required, rows = 4, placeholder, testId }) {
  return (
    <label className="block col-span-full">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-editorial text-base leading-relaxed resize-y"
      />
    </label>
  );
}
